import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  limit,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { UserReport, UserReportReason } from '../types';

export async function submitUserReport(input: {
  reporterUid: string;
  reporterName?: string;
  targetUserId: string;
  targetUserName?: string;
  postId?: string | null;
  reason: UserReportReason;
  details?: string;
}) {
  const reporterUid = String(input.reporterUid || '').trim();
  const targetUserId = String(input.targetUserId || '').trim();

  if (!reporterUid) throw new Error('Missing reporter uid');
  if (!targetUserId) throw new Error('Missing target user id');
  if (reporterUid === targetUserId) throw new Error('You cannot report yourself');

  const cleanDetails = String(input.details || '').trim();
  const payload = {
    reporterUid,
    reporterName: String(input.reporterName || '').trim() || null,
    targetUserId,
    targetUserName: String(input.targetUserName || '').trim() || null,
    postId: input.postId ? String(input.postId) : null,
    reason: input.reason,
    details: cleanDetails || null,
    status: 'open',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    createdAtServer: serverTimestamp(),
    updatedAtServer: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, 'reports'), payload);
  return ref.id;
}

export function subscribeOpenUserReports(
  onChange: (items: UserReport[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const qRef = query(collection(db, 'reports'), orderBy('createdAt', 'desc'), limit(200));
  return onSnapshot(
    qRef,
    (snap) => {
      const next: UserReport[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      }));
      onChange(next);
    },
    (err) => {
      if (onError) onError(err);
      else console.error('[reports] subscribeOpenUserReports error', err);
    },
  );
}

export async function setUserReportStatus(reportId: string, status: UserReport['status']) {
  await updateDoc(doc(db, 'reports', reportId), {
    status,
    updatedAt: Date.now(),
    updatedAtServer: serverTimestamp(),
  } as any);
}

export async function deleteUserReport(reportId: string) {
  await deleteDoc(doc(db, 'reports', reportId));
}
